/**
 * Issue deduplication helper used by UI layers that render validation
 * results (per-field error lists, banner summaries).
 *
 * The same logical issue is sometimes emitted by more than one rule —
 * typically once as a warning and once as an error — for the same field
 * and the same machine-readable code. Showing both confuses the user, so
 * we collapse them and keep only the most severe one.
 */

import { ValidationIssueGroup } from '@insio/shared-types';
import { Severity } from './types';

const SEVERITY_PRIORITY: Record<Severity, number> = {
  [Severity.error]: 2,
  [Severity.warning]: 1,
  [Severity.info]: 0
};

/** Minimal shape required to deduplicate — any structurally compatible type works. */
export interface DedupableIssue {
  severity: Severity | string;
  path?: string | null;
  code?: string | null;
  message?: string | null;
  groups?: readonly string[] | null;
}

function priority(severity: DedupableIssue['severity']): number {
  return SEVERITY_PRIORITY[severity as Severity] ?? 0;
}

function defaultKey(issue: DedupableIssue): string {
  // AI findings carry a model-authored message — never collapse them with the rule
  // issue sharing their code; identical repeated AI findings still collapse.
  if (issue.groups?.includes(ValidationIssueGroup.AI_ANALYSIS)) {
    return `${issue.path ?? ''}|${issue.code ?? ''}|${issue.message ?? ''}`;
  }
  return `${issue.path ?? ''}|${issue.code || issue.message || ''}`;
}

/**
 * Collapses issues that share the same key (by default `path|code`, falling
 * back to `message` when `code` is missing), keeping only the one with the
 * highest severity. Preserves the original order of the surviving issues.
 */
export function dedupeIssues<T extends DedupableIssue>(
  issues: readonly T[],
  keyFn: (issue: T) => string = defaultKey
): T[] {
  const winners = new Map<string, { issue: T; index: number }>();
  issues.forEach((issue, index) => {
    const key = keyFn(issue);
    const existing = winners.get(key);
    if (!existing || priority(issue.severity) > priority(existing.issue.severity)) {
      winners.set(key, { issue, index });
    }
  });
  return Array.from(winners.values())
    .sort((a, b) => a.index - b.index)
    .map(w => w.issue);
}
